import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/adminApi";
import { fmtRelative } from "../lib/format";
import { ArrowUpCircle, Check, Loader2 } from "lucide-react";

export default function UpgradeRequestsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bumping, setBumping] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/plans/upgrade-requests");
      setRows(res.data.items || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const quickBump = async (r) => {
    if (!window.confirm(`Change ${r.tenant_name} to "${r.requested_plan}"?`)) return;
    setBumping(r.id);
    try {
      await api.post(`/tenants/${r.tenant_id}/plan`, { plan: r.requested_plan });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed");
    } finally {
      setBumping(null);
    }
  };

  const pending = rows.filter((r) => r.status === "pending");
  const processed = rows.filter((r) => r.status !== "pending");

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto glass-animate" data-testid="upgrade-requests-page">
      <div className="flex items-center gap-2 mb-1">
        <ArrowUpCircle className="w-5 h-5 text-[#7C5CFF]" />
        <h1 className="text-2xl font-extrabold text-[#0B0F1A] tracking-tight">Upgrade Requests</h1>
      </div>
      <p className="text-sm text-[#5B617A] mb-5">{pending.length} pending · {processed.length} processed</p>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-[#7C5CFF] animate-spin" /></div>
      ) : (
        <>
          {/* Pending */}
          {pending.length > 0 && (
            <div className="mb-6">
              <p className="text-xs font-bold text-[#5B617A] uppercase tracking-wider mb-2 ml-1">Pending</p>
              <div className="glass-card overflow-hidden">
                {pending.map((r, i) => (
                  <div
                    key={r.id}
                    className={`flex items-center gap-3 px-4 py-3.5 ${i > 0 ? "border-t border-[#7C5CFF]/[0.06]" : ""}`}
                    data-testid={`pending-req-${r.id}`}
                  >
                    <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                      <ArrowUpCircle className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#0B0F1A] truncate">{r.tenant_name || r.tenant_id}</p>
                      <p className="text-[10px] text-[#5B617A]">
                        Wants <span className="font-semibold text-[#7C5CFF]">{r.requested_plan}</span>
                        {r.user_email && <> · Requested by {r.user_email}</>}
                        {r.note && <> · &quot;{r.note}&quot;</>}
                      </p>
                      <p className="text-[10px] text-[#5B617A]/50 mt-0.5">{fmtRelative(r.timestamp)}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        data-testid={`view-tenant-${r.id}`}
                        onClick={() => navigate(`/admin/tenants/${r.tenant_id}`)}
                        className="px-3 py-1.5 rounded-xl border border-[#7C5CFF]/[0.12] text-xs font-semibold text-[#0B0F1A] hover:bg-[#7C5CFF]/[0.06] transition-colors"
                      >
                        View
                      </button>
                      <button
                        data-testid={`bump-${r.id}`}
                        onClick={() => quickBump(r)}
                        disabled={bumping === r.id}
                        className="px-3 py-1.5 rounded-xl text-xs font-semibold text-white btn-primary-glass flex items-center gap-1"
                      >
                        {bumping === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        Bump to {r.requested_plan}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Processed */}
          {processed.length > 0 && (
            <div>
              <p className="text-xs font-bold text-[#5B617A] uppercase tracking-wider mb-2 ml-1">History</p>
              <div className="glass-card overflow-hidden">
                {processed.map((r, i) => (
                  <div
                    key={r.id}
                    className={`flex items-center gap-3 px-4 py-3 ${i > 0 ? "border-t border-[#7C5CFF]/[0.06]" : ""}`}
                    data-testid={`processed-req-${r.id}`}
                  >
                    <div className="w-8 h-8 rounded-xl bg-[#7C5CFF]/[0.08] flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4 text-[#5B617A]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0B0F1A] truncate">{r.tenant_name || r.tenant_id}</p>
                      <p className="text-[10px] text-[#5B617A]">
                        Requested <span className="font-semibold">{r.requested_plan}</span> · {r.status}
                        {r.user_email && <> · {r.user_email}</>}
                      </p>
                    </div>
                    <span className="text-[10px] text-[#5B617A]/50 shrink-0">{fmtRelative(r.timestamp)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {pending.length === 0 && processed.length === 0 && (
            <div className="text-center py-12">
              <ArrowUpCircle className="w-8 h-8 text-[#5B617A]/30 mx-auto mb-2" />
              <p className="text-[#5B617A]">No upgrade requests yet.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
